import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import ProfileAnalyticsCards from './components/ProfileAnalyticsCards';
import VisitorsList from './components/VisitorsList';
import SophisticatedMessaging from './components/SophisticatedMessaging';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import ExclusiveContent from './components/ExclusiveContent';
import NotificationSettings from './components/NotificationSettings';
import AlertPreferencesSettings from './components/AlertPreferencesSettings';
import { playerMessagesService } from '../../services/profileAnalyticsService';
import { visitorAnalyticsService } from '../../services/emailAlertService';

const ClientPortal = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewStats, setViewStats] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [loadingData, setLoadingData] = useState(false);

  const isPlayerCoachKeeper = ['player', 'coach', 'keeper']?.includes(userProfile?.role);
  const isScoutOrClub = ['scout', 'club']?.includes(userProfile?.role);

  useEffect(() => {
    if (userProfile && isPlayerCoachKeeper) {
      fetchDashboardData();
    }
  }, [userProfile, isPlayerCoachKeeper]);

  const fetchDashboardData = async () => {
    setLoadingData(true);
    try {
      // Fetch visitor analytics summary (admin-editable metrics)
      let analyticsData = null;
      try {
        const analyticsResponse = await visitorAnalyticsService?.getByCurrentUser();
        analyticsData = analyticsResponse?.data;
      } catch (analyticsError) {
        // Fallback: return zeroed defaults so dashboard still renders
        analyticsData = {
          totalViews: 0,
          scoutViews: 0,
          clubViews: 0,
          agentViews: 0,
          activeClubInterest: 0,
          totalScoutFeedback: 0,
          viewsLast7Days: 0,
          viewsLast30Days: 0
        };
      }
      setViewStats(analyticsData);
      
      // Fetch unread message count
      const unreadResponse = await playerMessagesService?.getUnreadCount();
      setUnreadMessageCount(unreadResponse?.count || 0);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare', badge: unreadMessageCount },
    { id: 'content', label: 'Exclusive Content', icon: 'Lock' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  // Filter tabs based on user role
  const visibleTabs = tabs?.filter(tab => {
    // Scouts and clubs don't see analytics or messages
    if (isScoutOrClub && (tab?.id === 'analytics' || tab?.id === 'messages')) {
      return false;
    }
    
    if (tab?.id === 'analytics' || tab?.id === 'messages') {
      return isPlayerCoachKeeper;
    }
    return true;
  });

  const handleLogout = async () => {
    await signOut();
    navigate('/sign-in');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #060810 0%, #0a1428 50%, #060810 100%)' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'rgba(255,107,0,0.6)', borderTopColor: 'transparent' }}></div>
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen pt-16" style={{ background: 'linear-gradient(135deg, #060810 0%, #0a1428 35%, #081a10 65%, #060810 100%)' }}>
        {/* Football field background patterns */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.025)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.02)' }} />
          <div className="absolute top-0 bottom-0 left-1/2 w-px" style={{ background: 'rgba(255,255,255,0.02)' }} />
          <div className="absolute left-0 right-0 top-1/2 h-px" style={{ background: 'rgba(255,255,255,0.02)' }} />
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 80px, rgba(255,255,255,0.008) 80px, rgba(255,255,255,0.008) 81px)' }} />
          {/* Adidas ball watermark */}
          <div className="absolute bottom-10 right-10 opacity-[0.03]">
            <svg viewBox="0 0 200 200" width="400" height="400" fill="white">
              <circle cx="100" cy="100" r="95" fill="none" stroke="white" strokeWidth="4"/>
              <polygon points="100,8 135,45 178,45 150,78 162,122 100,98 38,122 50,78 22,45 65,45" fill="white" opacity="0.7"/>
              <circle cx="100" cy="100" r="18" fill="white"/>
            </svg>
          </div>
          {/* Glow orbs */}
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(0,100,255,0.08) 0%, transparent 70%)' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(0,180,80,0.06) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Premium Header */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 overflow-hidden relative" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            {/* Header field lines */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.015) 60px, rgba(255,255,255,0.015) 61px)' }} />
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.6), transparent)' }} />
            </div>
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: 'rgba(255,107,0,0.8)' }}>Client Portal</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white mb-2">
                  Welcome Back, <span style={{ background: 'linear-gradient(135deg, #FF6B00, #FF9500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{userProfile?.full_name || user?.email}</span>
                </h1>
                <p className="text-sm md:text-base" style={{ color: 'rgba(180,210,255,0.7)' }}>
                  {userProfile?.role === 'scout' && 'Discover and track top talent across the globe'}
                  {userProfile?.role === 'club' && 'View player profiles and express interest in talent'}
                  {userProfile?.role === 'player' && 'Track your profile analytics and professional opportunities'}
                  {userProfile?.role === 'coach' && 'Monitor your profile visibility and career opportunities'}
                  {userProfile?.role === 'keeper' && 'Track your profile analytics and scout interest'}
                  {userProfile?.role === 'admin' && 'Manage platform content and user data'}
                </p>
                {userProfile?.organization && (
                  <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    <span className="font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>Organization:</span> {userProfile?.organization}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4">
                {/* PS5 + EA logos */}
                <div className="hidden md:flex items-center gap-3">
                  <div className="px-3 py-2 rounded-xl" style={{ background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.3)' }}>
                    <svg viewBox="0 0 90 30" width="72" height="24">
                      <text x="0" y="12" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                      <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="white">FC</text>
                      <rect x="24" y="17" width="66" height="2" fill="#FF6B00"/>
                    </svg>
                  </div>
                  <div className="px-3 py-2 rounded-xl" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.35)' }}>
                    <svg viewBox="0 0 55 24" width="44" height="19">
                      <text x="0" y="20" fontFamily="Arial Black, sans-serif" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
                    </svg>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:opacity-80"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="LogOut" size={16} color="rgba(255,255,255,0.7)" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="rounded-2xl p-4 sticky top-20" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {/* Adidas mini pattern */}
                <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <svg viewBox="0 0 30 20" width="24" height="16">
                    <polygon points="15,0 30,20 0,20" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5"/>
                    <polygon points="15,5 26,20 4,20" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
                    <polygon points="15,10 22,20 8,20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.3)' }}>Navigation</span>
                </div>
                <nav className="space-y-1.5">
                  {visibleTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300"
                      style={activeTab === tab?.id ? {
                        background: 'linear-gradient(135deg, rgba(255,107,0,0.25), rgba(255,149,0,0.15))',
                        border: '1px solid rgba(255,107,0,0.4)',
                        color: '#FF9500',
                        boxShadow: '0 0 20px rgba(255,107,0,0.15)'
                      } : {
                        background: 'transparent',
                        border: '1px solid transparent',
                        color: 'rgba(255,255,255,0.55)'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Icon name={tab?.icon} size={18} color={activeTab === tab?.id ? '#FF9500' : 'rgba(255,255,255,0.45)'} />
                        <span>{tab?.label}</span>
                      </div>
                      {tab?.badge > 0 && (
                        <span className="min-w-[20px] h-5 px-2 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'rgba(255,107,0,0.8)' }}>
                          {tab?.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="flex-1 space-y-6 md:space-y-8">
              {activeTab === 'dashboard' && (
                <div className="space-y-6">
                  {isPlayerCoachKeeper && (
                    <>
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-6 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B00, #FF9500)' }} />
                          <h2 className="text-xl md:text-2xl font-black text-white">Profile Analytics</h2>
                        </div>
                        <ProfileAnalyticsCards stats={viewStats} loading={loadingData} />
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <VisitorsList />
                        <div className="space-y-6">
                          <QuickActions userType={userProfile?.role} />
                          <RecentActivity userType={userProfile?.role} />
                        </div>
                      </div>
                    </>
                  )}

                  {isScoutOrClub && (
                    <div className="space-y-6">
                      <QuickActions userType={userProfile?.role} />
                      <RecentActivity userType={userProfile?.role} />
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && isPlayerCoachKeeper && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-6 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B00, #FF9500)' }} />
                      <h2 className="text-xl md:text-2xl font-black text-white">Detailed Analytics</h2>
                    </div>
                    <p className="mb-6" style={{ color: 'rgba(180,210,255,0.6)' }}>Comprehensive view of your profile performance</p>
                    <ProfileAnalyticsCards stats={viewStats} loading={loadingData} />
                  </div>
                  <VisitorsList />
                </div>
              )}

              {activeTab === 'messages' && isPlayerCoachKeeper && (
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-6 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B00, #FF9500)' }} />
                    <h2 className="text-xl md:text-2xl font-black text-white">Professional Messages</h2>
                  </div>
                  <p className="mb-6" style={{ color: 'rgba(180,210,255,0.6)' }}>Messages from clubs, scouts, and football professionals</p>
                  <SophisticatedMessaging />
                </div>
              )}

              {activeTab === 'content' && (
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-6 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B00, #FF9500)' }} />
                    <h2 className="text-xl md:text-2xl font-black text-white">Exclusive Content</h2>
                  </div>
                  <p className="mb-6" style={{ color: 'rgba(180,210,255,0.6)' }}>Premium resources and insights</p>
                  <ExclusiveContent userType={userProfile?.role} />
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-6 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B00, #FF9500)' }} />
                      <h2 className="text-xl md:text-2xl font-black text-white">Settings</h2>
                    </div>
                    <p className="mb-6" style={{ color: 'rgba(180,210,255,0.6)' }}>Manage your preferences and notifications</p>
                  </div>
                  <NotificationSettings />
                  <AlertPreferencesSettings />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientPortal;